import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spinner, Container, Row, Col } from 'react-bootstrap';
import { FaCalendar, FaUser, FaArrowLeft, FaClock, FaTag, FaShareAlt, FaArrowRight, FaGlobe } from 'react-icons/fa';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from './LanguageProvider';
import { useDispatch, useSelector } from 'react-redux';
import { Get_one_Blog_Action } from '../Redux/Actions/BlogsAction';
import SEO from './SEO';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export const BlogDetails = () => {
  const { id } = useParams();
  const { currentLang, isInitialized } = useLanguage();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sectionRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [blog, setBlog] = useState(null);
  const [imageError, setImageError] = useState(false);

  const langKey = (currentLang || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const isAR = langKey === 'ar';

  const selectblog = useSelector((state) => state.AllBlogs?.Blog);

  const getselectedblog = useCallback(async () => {
    try {
      setLoading(true);
      await dispatch(Get_one_Blog_Action(id));
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, id]);

  useEffect(() => {
    getselectedblog();
  }, [getselectedblog]);

  useEffect(() => {
    if (selectblog && selectblog.success) {
      setBlog(selectblog.data);
    }
  }, [selectblog]);

  useEffect(() => {
    if (!isInitialized || !blog) return;
    const ctx = gsap.context(() => {
      // Hero Content Reveal
      gsap.from('.article-hero-reveal', {
        y: 60, opacity: 0, duration: 1.2, stagger: 0.2, ease: 'power4.out'
      });

      // Parallax Image
      gsap.to('.article-hero-img', {
        scale: 1.2,
        scrollTrigger: {
          trigger: '.article-hero-section',
          start: 'top top',
          end: 'bottom top',
          scrub: true
        }
      });

      // Content Stagger
      gsap.from('.editorial-content-node', {
        y: 40, opacity: 0, duration: 1, stagger: 0.15, ease: 'power3.out',
        scrollTrigger: { trigger: '.article-body-wrap', start: 'top 85%' }
      });
    }, sectionRef);
    return () => ctx.revert();
  }, [isInitialized, blog, langKey]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(isAR ? 'ar-EG' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const calculateReadTime = (content) => {
    if (!content || typeof content !== 'string') return 1;
    const wordCount = content.trim().split(/\s+/).length;
    return Math.max(1, Math.ceil(wordCount / 200));
  };

  const translations = {
    en: {
      back: 'Return to Insights',
      readTime: 'min read',
      share: 'Share Insight',
      summary: 'Executive Summary',
      switchTo: 'Read in',
      loading: 'Preparing Insight...',
      error: 'Insight Not Found',
      published: 'Published'
    },
    ar: {
      back: 'العودة للمقالات',
      readTime: 'دقيقة للقراءة',
      share: 'مشاركة المقال',
      summary: 'ملخص تنفيذي',
      switchTo: 'اقرأ باللغة',
      loading: 'جاري تحضير المقال...',
      error: 'المقال غير موجود',
      published: 'نُشر في'
    }
  };

  const t = (key) => translations[langKey]?.[key] || translations.en[key] || key;

  const title = blog?.title?.[langKey] || blog?.title?.en || '';
  const description = blog?.short_description?.[langKey] || blog?.short_description?.en || '';
  const content = blog?.content?.[langKey] || blog?.content?.en || '';
  const blogImage = blog?.profileImg || blog?.image || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1920&q=80';
  const readTime = calculateReadTime(content);

  if (!isInitialized || loading) return (
    <div className="premium-fullscreen-loader">
      <div className="loader-orbit"></div>
      <p>{t('loading')}</p>
    </div>
  );

  if (!blog) return (
    <div className="text-center py-40">
      <h2>{t('error')}</h2>
      <Button variant="danger" onClick={() => navigate(-1)} className="mt-4">{t('back')}</Button>
    </div>
  );

  return (
    <div className="article-editorial-page" ref={sectionRef} dir={isAR ? 'rtl' : 'ltr'}>
      <SEO title={title} description={description} />
      
      <style>{`
        .article-editorial-page { font-family: var(--font-primary); color: #07111f; background: #fff; }
        
        .premium-fullscreen-loader {
          height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center;
          background: #fff; gap: 20px;
        }
        .loader-orbit {
          width: 40px; height: 40px; border: 2px solid #f1f5f9; border-top-color: #ae0001;
          border-radius: 50%; animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .premium-fullscreen-loader p { font-weight: 900; font-size: 11px; text-transform: uppercase; letter-spacing: 3px; color: #ae0001; }

        .article-hero-section {
          height: 85vh; paddingTop:"20px";min-height: 600px; position: relative; overflow: hidden;
          display: flex; align-items: center; justify-content: center; text-align: center; color: #fff;
        }
        .article-hero-img {
          position: absolute; inset: 0; 
          background: linear-gradient(rgba(7, 17, 31, 0.5), rgba(7, 17, 31, 0.85)), url('${imageError ? '/Flyvia Logo.png' : blogImage}');
          background-size: cover; background-position: center; z-index: 1;
        }
        .article-hero-overlay {
          position: absolute; bottom: 0; left: 0; width: 100%; height: 300px;
          background: linear-gradient(to top, #fff, transparent); z-index: 2;
        }
        
        .article-hero-inner { position: relative; z-index: 5; max-width: 1000px; padding: 0 30px; }
        .article-hero-meta {
          display: inline-flex; align-items: center; gap: 15px; margin-bottom: 30px;
          font-weight: 900; font-size: 13px; text-transform: uppercase; letter-spacing: 2px;
          background: rgba(255,255,255,0.1); padding: 8px 25px; border-radius: 50px; backdrop-filter: blur(10px);
        }
        .article-hero-title {
          font-family: var(--font-heading); font-size: clamp(25px, 6vw, 55px); font-weight: 900;
          line-height: 0.95; letter-spacing: -3px; margin-bottom: 30px;
        }

        .article-body-wrap { padding: 60px 0 120px; position: relative; }
        .editorial-container { max-width: 850px; margin: 0 auto; }

        .article-summary {
          background: #f8fafc; border: 1px solid #f1f5f9; padding: 45px; border-radius: 35px;
          margin-bottom: 60px; position: relative;
        }
        .article-summary h4 { 
          font-weight: 900; font-size: 12px; color: #ae0001; text-transform: uppercase; 
          letter-spacing: 2px; margin-bottom: 15px; display: block;
        }
        .article-summary p { font-size: 19px; color: #475569; line-height: 1.7; margin: 0; font-weight: 500; font-style: italic; }

        .article-main-content { font-size: 20px; line-height: 1.9; color: #1e293b; }
        .article-main-content p { margin-bottom: 35px; }
        .article-main-content h2 { 
          font-family: var(--font-heading); font-size: 36px; font-weight: 900; 
          margin: 60px 0 25px; letter-spacing: -1px; color: #07111f;
        }
        .article-main-content h3 { 
          font-family: var(--font-heading); font-size: 28px; font-weight: 900; 
          margin: 45px 0 20px; letter-spacing: -0.5px;
        }
        .article-main-content ul { margin-bottom: 35px; padding-inline-start: 25px; }
        .article-main-content li { margin-bottom: 15px; }

        .editorial-footer {
          margin-top: 80px; padding-top: 40px; border-top: 1px solid #f1f5f9;
          display: flex; justify-content: space-between; align-items: center;
        }
        .back-insight-btn {
          display: inline-flex; align-items: center; gap: 12px; font-weight: 900; font-size: 14px;
          text-transform: uppercase; color: #07111f; text-decoration: none; transition: 0.3s;
        }
        .back-insight-btn:hover { color: #ae0001; transform: ${isAR ? 'translateX(10px)' : 'translateX(-10px)'}; }
        
        .share-insight-btn {
          background: #ae0001; color: #fff; border: none; padding: 15px 35px; border-radius: 50px;
          font-weight: 900; font-size: 13px; text-transform: uppercase; letter-spacing: 1.5px;
          display: flex; align-items: center; gap: 12px; transition: 0.4s;
        }
        .share-insight-btn:hover { transform: translateY(-5px); box-shadow: 0 15px 35px #ae000133; }

        /* Mobile Fixes */
        @media (max-width: 992px) {
          .article-hero-section { height: 70vh; }
          .article-summary { padding: 30px; border-radius: 25px; }
          .article-main-content { font-size: 18px; }
        }
      `}</style>

      {/* Hero */}
      <section className="article-hero-section">
        <div className="article-hero-img">
          <img 
            src={blogImage} 
            alt="" 
            style={{ display: 'none' }} 
            onError={() => setImageError(true)} 
          />
        </div>
        <div className="article-hero-overlay" />
        <Container className="article-hero-inner">
          <div className="article-hero-reveal">
            <div className="article-hero-meta">
              <span>{formatDate(blog.createdAt)}</span>
              <span style={{ color: '#ae0001' }}>•</span>
              <span>{readTime} {t('readTime')}</span>
            </div>
            <h1 className="article-hero-title">{title}</h1>
            <div className="d-flex justify-content-center gap-3">
              <div 
              className='flex gap-2 items-center'
              style={{ background: 'rgba(255,255,255,0.1)', padding: '5px 20px', borderRadius: '50px', backdropFilter: 'blur(10px)', fontSize: '12px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1px' }}>
                <FaUser style={{ marginInlineEnd: '8px', color: '#ae0001' }} /> FlyVia Editorial
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Content */}
      <section className="article-body-wrap">
        <Container className="editorial-container">
          {description && (
            <div className="article-summary editorial-content-node">
              <h4>{t('summary')}</h4>
              <p>{description}</p>
            </div>
          )}

          <div className="article-main-content editorial-content-node">
            {content.split('\n').map((line, idx) => {
              if (line.startsWith('## ')) return <h2 key={idx}>{line.replace('## ', '')}</h2>;
              if (line.startsWith('### ')) return <h3 key={idx}>{line.replace('### ', '')}</h3>;
              if (line.trim().startsWith('- ')) return <li key={idx}>{line.trim().substring(2)}</li>;
              if (line.trim().length === 0) return <br key={idx} />;
              return <p key={idx}>{line}</p>;
            })}
          </div>

          <div className="editorial-footer editorial-content-node">
            <Link to="/MainBlogsCardspage" className="back-insight-btn">
              <FaArrowLeft style={{ transform: isAR ? 'rotate(180deg)' : 'none' }} />
              {t('back')}
            </Link>
            <button className="share-insight-btn">
              <FaShareAlt />
              {t('share')}
            </button>
          </div>
        </Container>
      </section>
    </div>
  );
};